import { CloseOutlined } from '@ant-design/icons';
import type { MouseEvent } from 'react';
import styled from 'styled-components';

import { CenterFlex, Divider, Flex } from '@/components/base';
import { Stage } from '@/components/Stage/Stage';
import Tooltip from '@/components/Tooltip/TooltipV2';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';
import type { STRUCTURED_FILTER_OPERATOR } from '@/types/structuredFilterConst';

import FieldItemOperatorSelector from './FieldItemOperatorSelector';

type Props = {
  filterIndex: number;
  isMultiValueField: boolean;
  columnName: string;
  filterQuery: StructuredFilterQueryFormatT;
  onOperatorChange?: (operator: STRUCTURED_FILTER_OPERATOR) => void;
  onClickValue?: (e: MouseEvent) => void;
  onRemoveClick?: (e: MouseEvent) => void;
};

const FieldItem = (props: Props) => {
  const color = useColor();
  const labelString = props.filterQuery.filterValues
    .map((value) => value.filterName)
    .join(', ');

  return (
    <Style>
      <SectionWrapper style={{ paddingLeft: 16 }}>
        <Typo type="label3" color={color.gray700}>
          {props.columnName}
        </Typo>
      </SectionWrapper>
      {!props.filterQuery.rangeValues && (
        <>
          <Divider direction="v" />
          <SectionWrapper>
            <FieldItemOperatorSelector
              dropdownKey={`filter-item-operator-selector-${props.filterQuery.columnKey}-${props.filterIndex}`}
              operator={props.filterQuery.operator}
              hasMultiValues={props.filterQuery.filterValues.length > 1}
              isMultiValueField={props.isMultiValueField}
              onChange={
                props.onOperatorChange
                  ? (v) => props.onOperatorChange?.(v)
                  : undefined
              }
            />
          </SectionWrapper>
        </>
      )}
      <Divider direction="v" />
      <Tooltip tooltipNode={labelString}>
        <SectionWrapper style={{ cursor: 'pointer' }}>
          <Stage>
            <Stage.Scene
              status={!!props.filterQuery.rangeValues}
              data={props.filterQuery.rangeValues}
            >
              {(rangeValue) => (
                <Typo
                  type="label3"
                  color={color.gray700}
                  ellipsis
                  style={{ maxWidth: 400 }}
                  onClick={props.onClickValue}
                >
                  {Number.isNaN(rangeValue.min) ? '' : rangeValue.min} ~{' '}
                  {Number.isNaN(rangeValue.max) ? '' : rangeValue.max}
                </Typo>
              )}
            </Stage.Scene>
            <Stage.Scene default>
              <Typo
                type="label3"
                color={color.gray700}
                ellipsis
                style={{ maxWidth: 400 }}
                onClick={props.onClickValue}
              >
                {props.filterQuery.filterValues
                  .map((value) => value.filterName)
                  .slice(0, 3)
                  .join(', ')}
                {props.filterQuery.filterValues.length > 3 && '...'}
              </Typo>
            </Stage.Scene>
          </Stage>
        </SectionWrapper>
      </Tooltip>
      <Divider direction="v" />
      <SectionWrapper
        style={{
          cursor: 'pointer',
          paddingRight: 12,
        }}
        onClick={props.onRemoveClick}
      >
        <CloseOutlined style={{ fontSize: 12 }} />
      </SectionWrapper>
    </Style>
  );
};

const Style = styled(Flex)`
  border: 1px solid ${({ theme }) => theme.colors.gray400};
  border-radius: 16px;
  min-height: 32px;
  overflow: hidden;
  align-items: stretch;
  background-color: ${({ theme }) => theme.colors.gray100};
`;

const SectionWrapper = styled(CenterFlex)`
  padding: 0px 8px;
  transition: 0.2s all;
  &:hover {
    background-color: ${({ theme }) => theme.colors.gray300};
  }
`;

export default FieldItem;
